import { computed, makeAutoObservable } from 'mobx';
import { Common } from 'src/AppState/Common';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { WebsocketV1 } from 'src/AppState/websocketV1/WebsocketV1';
import { GamstopNotificationType, NotificationGamstopChannelType } from 'src_server/types/notificationData';
import {
    filterVisibleGamstopNotifications,
    GAMSTOP_CHANNEL,
    getGamstopNotificationEntity,
    isGamstopNotification,
} from 'src/components/FraudAndCompliance/FraudAndCompliancesTab/FraudAndCompliances/components/gamstopNotificationUtils';

type GamstopNotificationActionType = 'assign' | 'unassign' | 'close';

export class GamstopTabState {
    private readonly notificationsResource: Resource<NotificationGamstopChannelType>;
    private readonly webSocketV1: WebsocketV1;

    public static get = AutoWeakMap.create((common: Common) => new GamstopTabState(common));

    private constructor(private readonly common: Common) {
        this.webSocketV1 = WebsocketV1.get(common);

        this.notificationsResource = new Resource(async (): Promise<NotificationGamstopChannelType> => {
            const notifications = await common.trpcClient.client.notification.getGamstopNotifications.query();
            return notifications;
        });

        const refresh = (): void => {
            this.refresh().catch((error) => {
                console.error('GamstopTabState refresh error', error);
            });
        };

        this.webSocketV1.subscribe(`S:Notification:${GAMSTOP_CHANNEL}`, refresh);
        makeAutoObservable(this);
    }

    @computed private get userId(): number | undefined {
        return this.common.session.userId ?? undefined;
    }

    @computed public get isLoading(): boolean {
        return this.notificationsResource.get().type === 'loading';
    }

    @computed public get hasError(): boolean {
        return this.notificationsResource.get().type === 'error';
    }

    @computed public get visibleNotifications(): Array<GamstopNotificationType> {
        const notifications = this.notificationsResource.getReady();
        if (notifications === null) {
            return [];
        }

        const gamstopNotifications: Array<GamstopNotificationType> = [];
        for (const notification of notifications) {
            if (isGamstopNotification(notification)) {
                gamstopNotifications.push(notification);
            }
        }

        return filterVisibleGamstopNotifications(gamstopNotifications);
    }

    @computed public get yourNotifications(): Array<GamstopNotificationType> {
        const userId = this.userId;
        if (userId === undefined) {
            return [];
        }

        return this.visibleNotifications.filter((notification) => notification.assignee?.id === userId);
    }

    @computed public get noneNotifications(): Array<GamstopNotificationType> {
        return this.visibleNotifications.filter(
            (notification) => notification.assignee?.id == null && notification.status !== 'assigned'
        );
    }

    @computed public get otherNotifications(): Array<GamstopNotificationType> {
        const userId = this.userId;
        if (userId === undefined) {
            return [];
        }

        return this.visibleNotifications.filter((notification) => {
            const assigneeId = notification.assignee?.id;
            return assigneeId !== undefined && assigneeId !== userId;
        });
    }

    public refresh = async (): Promise<void> => {
        await this.notificationsResource.refresh();
    };

    public updateNotification = async (
        notification: GamstopNotificationType,
        action: GamstopNotificationActionType
    ): Promise<void> => {
        const response = await this.common.trpcClient.client.notificationCounts.updateNotificationCounts.mutate({
            entity: getGamstopNotificationEntity(notification),
            action,
            id: notification.id,
        });

        if (response.responseStatus !== 'success') {
            console.error('GamstopTabState updateNotification error', response);
        }

        await this.refresh();
    };
}
